'use client';

import Image from '@/src/components/Image';
import { Routes } from '@/src/routes/routes';
import { ICmsBranch } from '../services/types';
import { cn, PRODUCT_IMAGE_SIZE } from '@/src/lib/utils/command';
import { Link } from '@/src/i18n/navigation';
import { FC } from 'react';
import { AmplitudeEvents } from '@/src/lib/amplitude/events';
import { trackEvent } from '@/src/lib/amplitude';
import Typography from '@/src/components/ui/typography';
import { Badge } from '@/src/components/ui/Badge';
import ExpressIcon from '@/src/components/icons/ExpressIcon';
import { useLocale } from 'next-intl';

type TProps = {
  store: ICmsBranch;
  className?: string;
  isNearby?: boolean;
  isPromoted?: boolean;
  liveDeliveryTime?: string;
};

const CmsStoreCard: FC<TProps> = ({ store, className, isNearby, isPromoted, liveDeliveryTime }) => {
  const displayTime = liveDeliveryTime;
  const locale = useLocale();
  const isRTL = locale === 'ar';
  const storeObj = (store as any)?.store ?? store;
  const storeId = storeObj?.id ?? storeObj?.StoreId ?? (store as any)?.id ?? '';
  const storeEnglishName =
    storeObj?.englishName ?? storeObj?.EnglishStoreName ?? storeObj?.name ?? 'store';
  const storeName = isRTL ? (storeObj?.arabicName ?? storeEnglishName) : storeEnglishName;
  const logoUrl = storeObj?.logoURL ?? storeObj?.logoUrl ?? storeObj?.StoreLogoUrl ?? '';
  const themeImage = storeObj?.theme?.Image ?? storeObj?.Theme?.Image ?? '';

  const branchUrl = storeId ? Routes.branchDetails(storeId, storeEnglishName) : '#';

  if (isPromoted) {
    return (
      <div className="relative aspect-square overflow-hidden rounded-3xl">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-2 h-full w-full"
          style={{
            background:
              'linear-gradient(179.95deg, rgba(0, 0, 0, 0.025) 0.04%, rgba(0, 0, 0, 0.9) 100.24%)',
          }}
        />

        <Image
          src={themeImage}
          alt={storeName}
          fill
          className="pointer-events-none z-1 object-cover"
          size={PRODUCT_IMAGE_SIZE.MEDIUM}
          sizes="(min-width: 1420px) 368px, (min-width: 1040px) calc(23.33vw + 42px), (min-width: 780px) calc(35.42vw - 23px), calc(61.09vw - 12px)"
        />

        <div className="relative z-3 flex h-full w-full flex-col justify-end p-2 lg:p-4">
          <div className="flex items-center gap-2">
            <Image
              src={logoUrl}
              alt={storeName}
              width={60}
              height={60}
              size={PRODUCT_IMAGE_SIZE.MEDIUM}
              className="h-15 w-15 rounded-2xl object-cover"
              quality={100}
            />

            <div className="flex flex-col items-start gap-1">
              <Typography
                variant={'body1'}
                component={'h3'}
                color={'white'}
                lineClamp={2}
                align={'start'}
              >
                {storeName}
              </Typography>

              {displayTime ? (
                <Badge variant="primary" className="mt-2 rounded-full py-0 text-xs font-medium">
                  <ExpressIcon className="size-3" />
                  {displayTime}
                </Badge>
              ) : null}
            </div>
          </div>
        </div>

        {storeId ? (
          <Link
            href={branchUrl}
            passHref
            onClick={() =>
              trackEvent(AmplitudeEvents.store_clicked, {
                shop_id: storeId,
                shop_name: storeEnglishName,
              })
            }
            className="absolute inset-0 z-5 h-full"
          />
        ) : null}
      </div>
    );
  }

  if (!storeId) {
    return (
      <div className={cn('relative aspect-square overflow-hidden rounded-2xl', className)}>
        <Image
          src={logoUrl}
          alt={storeEnglishName}
          size={PRODUCT_IMAGE_SIZE.MEDIUM}
          fill
          className="object-cover"
        />
      </div>
    );
  }

  return (
    <Link
      href={branchUrl}
      passHref
      onClick={() =>
        trackEvent(AmplitudeEvents.store_clicked, {
          shop_id: storeId,
          shop_name: storeEnglishName,
        })
      }
    >
      <div className={cn('relative aspect-square overflow-hidden rounded-2xl', className)}>
        <Image
          src={store.store.logoURL}
          alt={store.store?.englishName}
          size={PRODUCT_IMAGE_SIZE.MEDIUM}
          fill
          className="object-cover"
          sizes="(min-width: 1420px) 149px, (min-width: 1040px) calc(10vw + 9px), (min-width: 780px) calc(15.42vw - 16px), 28.7vw"
        />
      </div>
      <Typography
        variant={'body1'}
        component={'h3'}
        className="mt-2 px-2"
        align={'center'}
        weight={'medium'}
        lineClamp={2}
        color="black"
        leading={'tight'}
      >
        {storeName}
      </Typography>
      {isNearby && displayTime ? (
        <Badge
          variant="gray"
          className="mx-auto flex w-fit items-center justify-center rounded-full px-1 py-0 text-[10px] font-medium lg:text-xs"
        >
          <ExpressIcon className="" /> {displayTime}
        </Badge>
      ) : null}
    </Link>
  );
};

export default CmsStoreCard;
